import { useFormik } from 'formik'
import * as Yup from 'yup'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'
import { useEffect, useMemo, useState } from 'react'
import { AccountDetailsFormInitialData } from '../form/AccountDetailsForm'
import axiosClient from '../../../../../setup/axios/axiosClient'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../setup'

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  country: Yup.string().optional()
})

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

const useAccountDetails = () => {
  const [initialData, setInitialData] = useState<AccountDetailsFormInitialData>({
    firstName: '',
    lastName: '',
    country: '',
    address: {
      city: "",
      company: "",
      country: "",
      state: "",
      street: "",
      zip_code: ""
    }
  })
  const [email, setEmail] = useState("")
  const auth = useSelector(({ auth }: RootState) => auth.user)

  const [disableAddCreditsButton, setDisableAddCreditsButton] = useState(true)
  const formik = useFormik({
    initialValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      country: initialData?.country || '',
    },
    validationSchema,
    enableReinitialize: true, // Allows form to update if initialData loads via API
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateUserAccountDetails(values)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const countryList = useMemo(() => {
    return getCountries().map((code) => ({
      code,
      name: regionNames.of(code),
      dialCode: `+${getCountryCallingCode(code)}`,
    }))
  }, [])

  const fetchUserAccountDetails = async () => {
    try {
      const response = await axiosClient.get<{ userInfo: AccountDetailsFormInitialData, success: string }>(`/consumer/account-details`)
      if (!response.data?.userInfo) return

      const { country, firstName, lastName, address } = response.data?.userInfo
      if (country) setDisableAddCreditsButton(false)

      if (response.data) setInitialData({ country, firstName, lastName, address })
    } catch (error) {
      console.error('Error fetching account details:', error)
    }
  }


  const updateUserAccountDetails = async (data: Omit<AccountDetailsFormInitialData, "address">) => {
    try {
      const response = await axiosClient.put(
        `/consumer/update-account-details`,
        data
      )
      if (response.data) setInitialData(response.data.userInfo)
      if (response.data.message) toast.success(response.data.message)
    } catch (error) {
      console.error('Error fetching account details:', error)
    } finally {
      await fetchUserAccountDetails()
    }
  }

  const handleRedirectToPaymentPageBasedOnCountry = () => {
    const { country, firstName, lastName } = formik.values

    if (!country || !email || email === "") return

    let url = ''
    let query = `?first_name=${firstName}&last_name=${lastName}&email=${email}`

    const { address } = initialData

    if (address) {
      query += `&street=${address.street}&country=${country}&city=${address.city}&zip_code=${address.zip_code}&state_code=${address.state}&company=${address.company}`
    }

    if (country === 'IN') {
      url =
        'https://billing.nanothread.io/subscribe/69186d7ca8deed4a3208fdac/clan-meeting-credits-inr'
    } else {
      url =
        'https://billing.nanothread.io/subscribe/6918215e01ae194a1b613582/clan-meeting-credits-usd'
    }

    url += query

    window.open(url, '_blank', 'noopener,noreferrer')
  }


  useEffect(() => {
    fetchUserAccountDetails()
  }, [])

  useEffect(() => {
    if (auth?.user) {
      // @ts-ignore
      setEmail(auth.user.email)
    }
  }, [auth?.user])

  return {
    // Form State & Handlers
    formData: formik.values,
    handleChange: formik.handleChange,
    handleSubmit: formik.handleSubmit,
    handleBlur: formik.handleBlur,
    setFieldValue: formik.setFieldValue,

    resetForm: formik.resetForm,
    dirty: formik.dirty,

    // Validation State
    errors: formik.errors,
    touched: formik.touched,
    isValid: formik.isValid,
    isSubmitting: formik.isSubmitting,

    // Helpers
    countryList,

    disableAddCreditsButton,
    handleRedirectToPaymentPageBasedOnCountry
  }
}

export default useAccountDetails
